import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/auth/AuthProvider';
import { SubscriptionData, SubscriptionService } from '@/services/billing/SubscriptionService';

interface SubscriptionContextType {
  subscription: SubscriptionData | null;
  isLoading: boolean;
  isSubscribed: boolean;
  refetch: () => Promise<void>;
  invalidateAndRefetch: () => void;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

const AUTO_SYNC_KEY_PREFIX = "billing-auto-sync:";

const hasAutoSynced = (userId: string) => {
  try {
    return sessionStorage.getItem(AUTO_SYNC_KEY_PREFIX + userId) !== null;
  } catch {
    return false;
  }
};

const markAutoSynced = (userId: string) => {
  try {
    sessionStorage.setItem(AUTO_SYNC_KEY_PREFIX + userId, String(Date.now()));
  } catch {
    // Without storage the in-memory ref still limits it to once per page load.
  }
};

export const SubscriptionProvider = ({ children }: { children: ReactNode }) => {
  const { user, userProfile, loading: isUserLoading, refreshProfile } = useAuth();
  const queryClient = useQueryClient();
  const autoSyncedUserRef = useRef<string | null>(null);

  const fetchSubscription = useCallback(async (): Promise<SubscriptionData | null> => {
    if (!user) return null;
    return await SubscriptionService.getSubscription(user.id, undefined);
  }, [user]);

  const userId = user?.id;

  const {
    data: subscription,
    isLoading: isSubscriptionLoading,
    refetch: queryRefetch,
  } = useQuery({
    queryKey: ["subscription", userId],
    queryFn: fetchSubscription,
    enabled: !isUserLoading && !!userId,
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: true,
  });

  const refetch = useCallback(async () => {
    await queryRefetch();
  }, [queryRefetch]);

  const invalidateAndRefetch = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["subscription", userId] });
  }, [queryClient, userId]);

  // A profile with no Stripe customer may still have one that sales created in Stripe
  // under the same email. Look once per session so it shows up without anyone syncing.
  useEffect(() => {
    if (!userId || !userProfile || userProfile.stripe_id) return;
    if (autoSyncedUserRef.current === userId || hasAutoSynced(userId)) return;
    autoSyncedUserRef.current = userId;
    markAutoSynced(userId);

    SubscriptionService.syncBillingData()
      .then(async (result) => {
        if (result.synced === 0 && result.updated === 0) return;
        invalidateAndRefetch();
        await refreshProfile();
      })
      .catch((error) => console.warn("[Subscription] Automatic Stripe sync failed:", error));
  }, [userId, userProfile, invalidateAndRefetch, refreshProfile]);

  const contextValue = useMemo(() => {
    const isLoading = isUserLoading || isSubscriptionLoading;
    const isSubscribed = SubscriptionService.isSubscriptionActive(subscription);

    return {
      subscription,
      isLoading,
      isSubscribed,
      refetch,
      invalidateAndRefetch,
    };
  }, [subscription, isUserLoading, isSubscriptionLoading, refetch, invalidateAndRefetch]);

  return (
    <SubscriptionContext.Provider value={contextValue}>{children}</SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    // Return default values if provider is not available
    // This can happen during hot reload or component re-mounting
    return {
      subscription: null,
      isLoading: true,
      isSubscribed: false,
      refetch: async () => {},
      invalidateAndRefetch: () => {},
    };
  }
  return context;
};
